import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { ConfigService } from './config.service';

/**
 * Status of a Claude CLI auth session
 */
export type AuthStatus =
  | 'starting'      // Session is starting up
  | 'waiting_url'   // Waiting for OAuth URL to appear
  | 'waiting'       // OAuth URL captured, waiting for user
  | 'submitting'    // Code submitted, waiting for completion
  | 'authenticated' // Success!
  | 'failed'        // Auth failed
  | 'cancelled';    // User cancelled

/**
 * Diagnostic information for debugging auth issues
 */
export interface AuthDiagnostics {
  cli_path: string | null;
  cli_version: string | null;
  cli_found: boolean;
  ssh_bypass: boolean;
  trust_established: boolean;
  process_started: boolean;
  process_pid: number | null;
  process_exit_code: number | null;
  url_captured: boolean;
  url_capture_time_ms: number | null;
  code_submitted: boolean;
  code_submit_time_ms: number | null;
  credentials_found: boolean;
  credentials_check_count: number;
  raw_output_lines: number;
  raw_output_tail: string[];
  errors: string[];
  warnings: string[];
}

/**
 * Response from auth endpoints
 */
export interface AuthSessionResponse {
  session_id: string;
  status: AuthStatus;
  oauth_url: string | null;
  error: string | null;
  diagnostics?: AuthDiagnostics;
}

/**
 * Response from submit-code endpoint
 */
export interface SubmitCodeResponse {
  success: boolean;
  message?: string;
  error?: string;
  diagnostics?: AuthDiagnostics;
}

/**
 * Service for managing Claude CLI OAuth authentication wizard.
 *
 * This service provides a terminal-free way to authenticate Claude CLI.
 * The flow:
 * 1. Call startAuth() - backend starts Claude CLI and captures OAuth URL
 * 2. Open the OAuth URL in a new browser tab
 * 3. User completes OAuth in their browser and gets a code
 * 4. Call submitCode() with the code
 * 5. Poll with pollStatus() until authenticated
 *
 * Usage:
 * ```typescript
 * const wizard = inject(ClaudeAuthWizardService);
 *
 * // Start auth
 * const session = await wizard.startAuth();
 * if (session.oauth_url) {
 *   window.open(session.oauth_url, '_blank');
 * }
 *
 * // Subscribe to status updates
 * wizard.authStatus$.subscribe(status => {
 *   if (status === 'authenticated') {
 *     // Success!
 *   }
 * });
 *
 * // Start polling (auto-stops when authenticated/failed/cancelled)
 * wizard.startPolling();
 *
 * // Or submit code and poll manually
 * const result = await wizard.submitCode(code);
 * if (result.success) {
 *   // Check diagnostics for details
 *   console.log(result.diagnostics);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ClaudeAuthWizardService {
  /** Current auth session ID */
  private _sessionId: string | null = null;

  /** Current OAuth URL (if captured) */
  private _oauthUrl: string | null = null;

  /** Latest diagnostics */
  private _diagnostics: AuthDiagnostics | null = null;

  /** Observable auth status */
  private _authStatus = new BehaviorSubject<AuthStatus | null>(null);
  public authStatus$ = this._authStatus.asObservable();

  /** Observable error message */
  private _error = new BehaviorSubject<string | null>(null);
  public error$ = this._error.asObservable();

  /** Observable diagnostics */
  private _diagnostics$ = new BehaviorSubject<AuthDiagnostics | null>(null);
  public diagnostics$ = this._diagnostics$.asObservable();

  /** Polling subscription */
  private _pollingSub: Subscription | null = null;

  /** Polling interval in ms */
  private readonly POLL_INTERVAL_MS = 2000;

  constructor(private http: HttpClient) {}

  /**
   * Get current session ID
   */
  get sessionId(): string | null {
    return this._sessionId;
  }

  /**
   * Get current OAuth URL
   */
  get oauthUrl(): string | null {
    return this._oauthUrl;
  }

  /**
   * Get current auth status
   */
  get currentStatus(): AuthStatus | null {
    return this._authStatus.value;
  }

  /**
   * Get latest diagnostics
   */
  get diagnostics(): AuthDiagnostics | null {
    return this._diagnostics;
  }

  /**
   * Check if auth is in progress
   */
  get isAuthInProgress(): boolean {
    const status = this._authStatus.value;
    return status === 'starting' || status === 'waiting_url' || status === 'waiting' || status === 'submitting';
  }

  /**
   * Start a new Claude CLI auth session.
   *
   * This calls the backend which starts Claude CLI and captures the OAuth URL.
   * If successful, returns the OAuth URL for the user to visit.
   *
   * @returns AuthSessionResponse with session_id and oauth_url
   * @throws HttpErrorResponse on API error
   */
  async startAuth(credentialsId?: string, team?: string | null): Promise<AuthSessionResponse> {
    // Reset state
    this._sessionId = null;
    this._oauthUrl = null;
    this._diagnostics = null;
    this._error.next(null);
    this._authStatus.next('starting');
    this._diagnostics$.next(null);
    this.stopPolling();

    try {
      const params: any = {};
      if (credentialsId) {
        params.credentials_id = credentialsId;
      }
      // Team-scoped credentials: the token must land beside the credential's
      // metadata, not in the flat legacy dir (split credential, 2026-09-01).
      if (team) params.team = team;
      const response = await lastValueFrom(
        this.http.post<AuthSessionResponse>(
          `${ConfigService.muxUrl}/backends/claude-cli/auth/start`,
          {},
          { params }
        )
      );

      this._sessionId = response.session_id;
      this._oauthUrl = response.oauth_url;
      this._authStatus.next(response.status);

      if (response.diagnostics) {
        this._diagnostics = response.diagnostics;
        this._diagnostics$.next(response.diagnostics);
      }

      if (response.error) {
        this._error.next(response.error);
      }

      return response;
    } catch (error: any) {
      this._authStatus.next('failed');
      const errorMsg = error?.error?.detail || error?.message || 'Failed to start auth';
      this._error.next(errorMsg);
      throw error;
    }
  }

  /**
   * Poll the auth status once.
   *
   * @param includeDiagnostics If true, fetch full diagnostics (slower)
   * @returns Current AuthSessionResponse
   * @throws HttpErrorResponse on API error
   */
  async pollStatus(includeDiagnostics: boolean = false): Promise<AuthSessionResponse | null> {
    if (!this._sessionId) {
      return null;
    }

    try {
      const params: any = { session_id: this._sessionId };
      if (includeDiagnostics) {
        params.include_diagnostics = 'true';
      }

      const response = await lastValueFrom(
        this.http.get<AuthSessionResponse>(
          `${ConfigService.muxUrl}/backends/claude-cli/auth/status`,
          { params }
        )
      );

      this._authStatus.next(response.status);

      if (response.diagnostics) {
        this._diagnostics = response.diagnostics;
        this._diagnostics$.next(response.diagnostics);
      }

      if (response.error) {
        this._error.next(response.error);
      }

      return response;
    } catch (error: any) {
      // 404 means session expired/not found
      if (error?.status === 404) {
        this._authStatus.next('failed');
        this._error.next('Auth session expired');
      }
      throw error;
    }
  }

  /**
   * Get the captured terminal output from the auth session.
   *
   * Useful for debugging and for IT support to see what's happening.
   *
   * @param tailLines Number of lines to return (default 50)
   * @returns Terminal output string
   */
  async getOutput(tailLines: number = 50): Promise<string | null> {
    if (!this._sessionId) {
      return null;
    }

    try {
      const response = await lastValueFrom(
        this.http.get<{ output: string }>(
          `${ConfigService.muxUrl}/backends/claude-cli/auth/output`,
          { params: { session_id: this._sessionId, tail_lines: tailLines.toString() } }
        )
      );
      return response.output;
    } catch (error) {
      console.error('Failed to get auth output:', error);
      return null;
    }
  }

  /**
   * Get full diagnostics for the current session.
   *
   * @returns Full diagnostic information
   */
  async fetchDiagnostics(): Promise<AuthDiagnostics | null> {
    if (!this._sessionId) {
      return null;
    }

    try {
      const response = await lastValueFrom(
        this.http.get<{ diagnostics: AuthDiagnostics }>(
          `${ConfigService.muxUrl}/backends/claude-cli/auth/diagnostics`,
          { params: { session_id: this._sessionId } }
        )
      );
      this._diagnostics = response.diagnostics;
      this._diagnostics$.next(response.diagnostics);
      return response.diagnostics;
    } catch (error) {
      console.error('Failed to get auth diagnostics:', error);
      return null;
    }
  }

  /**
   * Start automatic polling for auth status.
   *
   * Polls every 2 seconds until status is terminal (authenticated/failed/cancelled).
   * Automatically stops when terminal state reached.
   */
  startPolling(): void {
    if (this._pollingSub) {
      return; // Already polling
    }

    if (!this._sessionId) {
      console.warn('Cannot start polling: no active auth session');
      return;
    }

    this._pollingSub = interval(this.POLL_INTERVAL_MS)
      .pipe(
        takeWhile(() => this.isAuthInProgress)
      )
      .subscribe(async () => {
        try {
          await this.pollStatus();
        } catch (error) {
          console.error('Polling error:', error);
          this.stopPolling();
        }
      });
  }

  /**
   * Stop polling for auth status.
   */
  stopPolling(): void {
    if (this._pollingSub) {
      this._pollingSub.unsubscribe();
      this._pollingSub = null;
    }
  }

  /**
   * Submit the OAuth code to complete authentication.
   *
   * After the user completes OAuth in the browser, they receive a code
   * that must be submitted back to complete authentication.
   *
   * @param code The OAuth code from the browser
   * @returns Promise with success status and diagnostics
   */
  async submitCode(code: string): Promise<SubmitCodeResponse> {
    if (!this._sessionId) {
      return { success: false, error: 'No active auth session' };
    }

    try {
      const response = await lastValueFrom(
        this.http.post<SubmitCodeResponse>(
          `${ConfigService.muxUrl}/backends/claude-cli/auth/submit-code`,
          { session_id: this._sessionId, code }
        )
      );

      // Update diagnostics if provided
      if (response.diagnostics) {
        this._diagnostics = response.diagnostics;
        this._diagnostics$.next(response.diagnostics);
      }

      // If successful, poll status to update state
      if (response.success) {
        await this.pollStatus();
      } else {
        // Set error from response
        if (response.error) {
          this._error.next(response.error);
        }
      }

      return response;
    } catch (error: any) {
      // Handle FastAPI validation errors (which return detail as array) and regular errors
      let errorMsg = 'Failed to submit code';
      if (error?.error?.detail) {
        if (Array.isArray(error.error.detail)) {
          errorMsg = error.error.detail.map((d: any) => d.msg || d).join(', ');
        } else {
          errorMsg = String(error.error.detail);
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }
      this._error.next(errorMsg);

      // Try to extract diagnostics from error response
      if (error?.error?.diagnostics) {
        this._diagnostics = error.error.diagnostics;
        this._diagnostics$.next(error.error.diagnostics);
      }

      return { success: false, error: errorMsg, diagnostics: this._diagnostics ?? undefined };
    }
  }

  /**
   * Cancel the current auth session.
   *
   * This terminates the background Claude CLI process.
   */
  async cancelAuth(): Promise<void> {
    if (!this._sessionId) {
      return;
    }

    this.stopPolling();

    try {
      await lastValueFrom(
        this.http.post(
          `${ConfigService.muxUrl}/backends/claude-cli/auth/cancel`,
          null,
          { params: { session_id: this._sessionId } }
        )
      );
    } catch (error) {
      console.error('Failed to cancel auth:', error);
    }

    this._sessionId = null;
    this._oauthUrl = null;
    this._authStatus.next('cancelled');
  }

  /**
   * Reset service state.
   *
   * Call this when navigating away or on component destroy.
   */
  reset(): void {
    this.stopPolling();
    this._sessionId = null;
    this._oauthUrl = null;
    this._diagnostics = null;
    this._authStatus.next(null);
    this._error.next(null);
    this._diagnostics$.next(null);
  }
}
